import { useApiSegmentsRange } from '@app/core/hooks/api/useApiSegmentsRange';
import { DEFAULT_TIME_RANGE_MS, SEGMENTS_REFETCH_INTERVAL_MS } from '../constants';

interface Range {
  readonly from: string | number;
  readonly to: string | number;
}

interface UseLatestSegmentsParams {
  readonly stationId: string;
  readonly enabled?: boolean;
  readonly range?: Range;
}

/**
 * Hook to fetch segments for a station using React Query.
 * If no range is provided, defaults to [-6 minutes, now].
 */
export function useLatestSegments(params: UseLatestSegmentsParams) {
  const { stationId, enabled, range } = params;

  const timeRange = (() => {
    if (range) {
      return {
        from: new Date(range.from).valueOf(),
        to: new Date(range.to).valueOf(),
      };
    }

    const now = Date.now();
    return {
      from: now - DEFAULT_TIME_RANGE_MS,
      to: now,
    };
  })();

  const query = useApiSegmentsRange(stationId, timeRange.from, timeRange.to, {
    enabled: enabled ?? true,
    staleTime: 0,
    refetchInterval: SEGMENTS_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: true,
  });

  return {
    data: query.data ?? [],
    isLoading: query.data ? false : query.isLoading || !enabled,
    error: query.error as Error | null,
    refetch: query.refetch,
    timeRange: {
      from: timeRange.from,
      to: timeRange.to,
    },
  };
}

useLatestSegments.queryKey = (stationId: string, from: string | number, to: string | number) =>
  useApiSegmentsRange.queryKey(stationId, from, to);
